import React, { useState, useEffect, useRef, createContext } from "react";
import Genres, {
  genreAll,
  genreNone,
  genreOther,
  genreOtherSlug,
  getGenreIds,
} from "./Genres";
import { TermData } from "../Term";
import Pagenation from "../Pagenation";
import Shops from "./Shops";
import { ShopData } from "./Shop";
import ShopSearchForm from "../SearchForm";
import ErrorBoundary from "../ErrorBoundary";
import { Oval } from "react-loader-spinner";
import {
  getMaxPageFromResponse,
  isSmartPhone,
  removeTrailingNumbers,
} from "../utility";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { REST_PATH } from "../variables";
import { shopArchiveURIs } from "./variables";

const perPage = isSmartPhone() ? 7 : 5;
const shopURI = `${REST_PATH}/shop?per_page=${perPage}`;

declare const genres: TermData[]; // phpでジャンル一覧を定義済み
export const GenresContext = createContext(genres);

type Param = {
  genreSlug?: string;
  searchText?: string;
  pageNum?: string;
};

const getGenreQuery = (genreSlug: string): string => {
  switch (genreSlug) {
    case "":
      return "";
    case genreOtherSlug: // どのジャンルでもない
      return `&genre_exclude=${getGenreIds(genres).join(",")}`;
    default:
      return `&genre=${getGenreIdFromSlug(genreSlug)}`;
  }
};

const getGenreIdFromSlug = (genreSlug?: string): number => {
  if (genreSlug == "" || genreSlug === undefined) return genreAll;
  if (genreSlug == genreOtherSlug) return genreOther;
  const id = genres.find((genre) => genreSlug == genre.slug)?.id;
  return id || genreNone;
};

const ShopArchive: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [shops, setShops] = useState<ShopData[]>([]); // ショップ一覧
  const [totalPageCount, setTotalPageCount] = useState(1); // ショップ一覧のページ数
  const [page, setPage] = useState(1); // 現在のページング
  const params: Param = useParams<Param>(); // React Routerからのパラメータ
  const myElementRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const genreQuery = params.genreSlug ? getGenreQuery(params.genreSlug) : "";
    const searchQuery = params.searchText ? `&search=${params.searchText}` : "";
    const pageQuery = params.pageNum ? `&page=${params.pageNum}` : "";
    params.pageNum && setPage(Number(params.pageNum));
    try {
      fetchShops(`${shopURI}${genreQuery}${searchQuery}${pageQuery}`);
    } catch (e) {
      console.error(`Error: ${e}`);
      fetchShops(shopURI);
    }
  }, [params]);

  const fetchShops = async (url: string) => {
    setIsLoading(true);
    try {
      const res = await fetch(url);
      const json = await res.json();
      setTotalPageCount(getMaxPageFromResponse(res, perPage));
      setShops(json);
    } catch (e) {
      throw e;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (searchText: string) => {
    navigate(`${shopArchiveURIs.search}${encodeURIComponent(searchText)}`);
  };

  // ページネーションクリック時に上までスクロール
  const handlePagenationClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (myElementRef.current) {
      myElementRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const getPagenationBaseUrl = (): string =>
    params.pageNum
      ? removeTrailingNumbers(location.pathname)
      : location.pathname + "/page/";

  return (
    <>
      <div className="genre-block" ref={myElementRef}>
        <ErrorBoundary>
          <ShopSearchForm
            value={params.searchText || ""}
            onClick={handleSearch}
          />
          <Genres genres={genres} />
        </ErrorBoundary>
      </div>

      {isLoading ? (
        <Oval color="#198B86" />
      ) : (
        <ErrorBoundary>
          <GenresContext.Provider value={genres}>
            <Shops shops={shops} />
          </GenresContext.Provider>

          <Pagenation
            currentPage={page}
            pageCount={totalPageCount}
            baseUrl={getPagenationBaseUrl()}
            onClick={handlePagenationClick}
          />
        </ErrorBoundary>
      )}
    </>
  );
};

export default ShopArchive;
